import { formComponents as fc } from "@/features/i18n/formComponents";
import { ui } from "@patient-watch/ui";
import { authQueryOptions } from "@/features/auth/api/useAuthQuery";
import { invalidateProfileQuery, patchProfileQueryData } from "@/features/profile/api/selectProfile";
import OnboardingEmailForm from "@/features/onboarding/components/OnboardingEmailForm";
import { supabaseClient } from "@/lib/supabase";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { toast } from "@patient-watch/ui/sonner";
import * as z from "zod";
import { useProfileCopy } from "@/features/i18n/profileCopy";

interface UpdateEmailCopy {
    validEmailAddress: string;
    accountSignInEmailRequired: string;
    newEmailDifferentFromSignIn: string;
    newEmailDifferentFromCurrent: string;
}

function buildFormSchema(profileEmailNorm: string, allowStaffAuthOverride: boolean, copy: UpdateEmailCopy) {
    return z
        .object({
            new_email: z.string().email(copy.validEmailAddress),
            auth_old_email: z.string(),
        })
        .superRefine((data, ctx) => {
            const override = allowStaffAuthOverride ? (data.auth_old_email?.trim() ?? "") : "";
            if (override && !z.string().email().safeParse(override).success) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: copy.validEmailAddress,
                    path: ["auth_old_email"],
                });
                return;
            }
            if (!profileEmailNorm && !override) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: copy.accountSignInEmailRequired,
                    path: ["auth_old_email"],
                });
                return;
            }
            if (override && data.new_email.trim().toLowerCase() === override.toLowerCase()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: copy.newEmailDifferentFromSignIn,
                    path: ["new_email"],
                });
                return;
            }
            if (!override && data.new_email.trim().toLowerCase() === profileEmailNorm) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: copy.newEmailDifferentFromCurrent,
                    path: ["new_email"],
                });
            }
        });
}

type FormSchemaType = z.infer<ReturnType<typeof buildFormSchema>>;

/** The session-email query can return the CALLER's own cached session even in admin/override mode
 * (react-query enabled:false only skips a new fetch, not already-cached data for that key). Only
 * trust it as this profile's own sign-in email in self-service mode - otherwise editing someone
 * else's email silently renames the caller's own account instead (see
 * clinician-secretary-management-concerns.md, 2026-09-01). */
export function trustedSessionEmail(allowAuthEmailOverride: boolean, sessionEmail: string): string {
    return allowAuthEmailOverride ? "" : sessionEmail;
}

interface Props {
    currentEmail: string | null;
    userId: string;
    onSuccess?: () => void;
    /**
     * Staff / diary edit: show optional field when Auth email may differ from profile.email,
     * or allow change when profile has no email but the account does.
     */
    allowAuthEmailOverride?: boolean;
}

const UpdateEmailForm = ({ currentEmail, userId, onSuccess, allowAuthEmailOverride = false }: Props) => {
    const copy = useProfileCopy();
    const queryClient = useQueryClient();
    const oldEmailTrimmed = currentEmail?.trim() ?? "";
    const { data: authUser, isPending: isAuthPending } = useQuery({
        ...authQueryOptions(),
        enabled: !allowAuthEmailOverride,
    });
    const sessionEmailTrimmed = trustedSessionEmail(allowAuthEmailOverride, authUser?.email?.trim() ?? "");
    const signInEmailTrimmed = sessionEmailTrimmed || oldEmailTrimmed;
    const signInEmailNorm = signInEmailTrimmed.toLowerCase();

    const formSchema = useMemo(
        () => buildFormSchema(signInEmailNorm, allowAuthEmailOverride, copy),
        [signInEmailNorm, allowAuthEmailOverride, copy]
    );

    const form = useForm<FormSchemaType>({
        resolver: zodResolver(formSchema),
        defaultValues: { new_email: "", auth_old_email: "" },
    });

    useEffect(() => {
        form.reset({ new_email: "", auth_old_email: "" });
    }, [signInEmailNorm, allowAuthEmailOverride, form]);

    const updateEmailMutation = useMutation({
        mutationFn: async ({ new_email, auth_old_email }: FormSchemaType) => {
            const override = allowAuthEmailOverride ? (auth_old_email?.trim() ?? "") : "";
            const old_email = override || sessionEmailTrimmed || oldEmailTrimmed;
            if (!old_email) {
                throw new Error(copy.noSignInEmailToChange);
            }
            const { data, error } = await supabaseClient.functions.invoke("public-update-user-email", {
                body: { old_email, new_email },
            });
            if (error) throw error;
            if (data?.error) throw new Error(data.error);
            return data;
        },
        onSuccess: (_data, variables) => {
            toast.success(copy.emailUpdatedSuccessfully);
            patchProfileQueryData(queryClient, userId, { email: variables.new_email.trim() });
            void invalidateProfileQuery(queryClient, userId);
            void queryClient.invalidateQueries({ queryKey: ["profiles"] });
            void queryClient.invalidateQueries({ queryKey: ["diary"] });
            onSuccess?.();
        },
        onError: (error) => {
            console.error("Error updating email:", error);
            toast.error(copy.emailUpdateFailed);
        },
    });

    const syncProfileEmailMutation = useMutation({
        mutationFn: async (emailToSync: string) => {
            await supabaseClient
                .from("users")
                .update({ email: emailToSync })
                .eq("user_id", userId)
                .throwOnError()
                .select()
                .single();
        },
        onSuccess: (_data, emailToSync) => {
            toast.success(copy.emailSavedToProfile);
            patchProfileQueryData(queryClient, userId, { email: emailToSync });
            void invalidateProfileQuery(queryClient, userId);
            void queryClient.invalidateQueries({ queryKey: ["profiles"] });
            void queryClient.invalidateQueries({ queryKey: ["diary"] });
            void queryClient.invalidateQueries({ queryKey: ["user"] });
            onSuccess?.();
        },
        onError: (error) => {
            console.error("Error syncing profile email:", error);
            toast.error(copy.emailSaveToProfileFailed);
        },
    });

    const onSubmit = (data: FormSchemaType) => {
        updateEmailMutation.mutate({
            new_email: data.new_email.trim(),
            auth_old_email: data.auth_old_email,
        });
    };

    if (!oldEmailTrimmed && !allowAuthEmailOverride) {
        if (isAuthPending) {
            return <p className="text-muted-foreground text-sm">{copy.loading}</p>;
        }

        if (sessionEmailTrimmed) {
            return (
                <div className="space-y-4">
                    <p className="text-muted-foreground text-sm leading-relaxed">
                        {copy.accountSignInEmailIntro(sessionEmailTrimmed)}
                    </p>
                    <ui.Button
                        className="w-full"
                        disabled={syncProfileEmailMutation.isPending}
                        type="button"
                        onClick={() => syncProfileEmailMutation.mutate(sessionEmailTrimmed)}
                    >
                        {syncProfileEmailMutation.isPending ? copy.saving : copy.saveEmailToProfile}
                    </ui.Button>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                        {copy.saveEmailFirstDescription}{" "}
                        <Link className="font-medium text-foreground underline underline-offset-4" to="/profile/email">
                            {copy.profileEmailLink}
                        </Link>
                        .
                    </p>
                </div>
            );
        }

        return (
            <div className="space-y-3">
                <p className="text-muted-foreground text-sm leading-relaxed">{copy.noProfileEmailAddDescription}</p>
                <OnboardingEmailForm
                    currentEmail={null}
                    userId={userId}
                    onSuccess={() => {
                        void queryClient.invalidateQueries({ queryKey: ["user"] });
                        onSuccess?.();
                    }}
                />
            </div>
        );
    }

    return (
        <ui.Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="form">
                {oldEmailTrimmed ? (
                    <p className="mb-4 text-muted-foreground text-sm">
                        {copy.profileEmailIntro(oldEmailTrimmed)} {copy.profileEmailIntroSuffix}
                        {allowAuthEmailOverride ? <> {copy.signInEmailDiffersHelp}</> : null}
                    </p>
                ) : (
                    <p className="mb-4 text-muted-foreground text-sm">{copy.noProfileEmailReplaceDescription}</p>
                )}
                {allowAuthEmailOverride ? (
                    <fc.Input
                        name="auth_old_email"
                        label={oldEmailTrimmed ? copy.signInEmailOnlyIfDifferent : copy.signInEmail}
                        placeholder={copy.leaveBlankIfSameAsProfile}
                    />
                ) : null}
                <fc.Input name="new_email" label={copy.newEmail} placeholder={copy.emailAddressToChangeTo} />

                <fc.SaveButton
                    label={copy.save}
                    loadingText={copy.saving}
                    isLoading={updateEmailMutation.isPending}
                    className="w-full"
                />
            </form>
        </ui.Form>
    );
};

export default UpdateEmailForm;
